import { ListPagination } from "./NotificationListControls";
import { BENEFIT_TEMPLATE_BLUEPRINTS } from "./benefitTemplateBlueprints";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import ButtonsEditor, { KakaoButton } from "../../../Components/Common/ButtonsEditor";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "../../../Components/ui";
import { useAuthContext } from "../../../Components/AuthContext";
import {
  AlimTalkAccountMode,
  AlimTalkAccountResponse,
  AlimTalkProviderTemplateCatalogItem,
  AlimTalkTemplate,
  AlimTalkTemplateBlueprint,
  AlimTalkTemplateUpsertPayload,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import { hasCapability } from "../../../common/permissions";
import { dialogService } from "../../../utils/dialogService";
import {
  getAlimTalkTemplateContent,
  getAlimTalkTemplateName,
  getAlimTalkTemplateStatus,
  getAlimTalkTemplateVariables,
  normalizeAlimTalkAccountMode,
} from "./alimtalkTemplate";

type TemplateForm = {
  template_code: string;
  template_name: string;
  template_content: string;
  template_type: string;
  category_code: string;
  is_secure_template: boolean;
  buttons: KakaoButton[];
};

const EMPTY_FORM: TemplateForm = {
  template_code: "",
  template_name: "",
  template_content: "",
  template_type: "BA",
  category_code: "008001",
  is_secure_template: false,
  buttons: [],
};

const asTemplateList = (data: { results?: AlimTalkTemplate[] } | AlimTalkTemplate[] | undefined) => (
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []
);

const getStatusBadge = (status: string) => {
  const meta: Record<string, { label: string; color: string }> = {
    REG: { label: "등록", color: "secondary" },
    REQ: { label: "검수 요청", color: "warning" },
    APR: { label: "승인", color: "success" },
    REJ: { label: "반려", color: "danger" },
    STP: { label: "중단", color: "dark" },
    ARCHIVED: { label: "보관", color: "secondary" },
  };
  const item = meta[status] || { label: status || "상태 미확인", color: "secondary" };
  return <Badge color={item.color}>{item.label}</Badge>;
};

const getErrorMessage = (error: any, fallback: string) => (
  error?.response?.data?.detail || error?.response?.data?.error || error?.message || fallback
);

type AlimTalkTemplateManagementProps = {
  view?: "site" | "headquarters";
  accountModeOverride?: AlimTalkAccountMode;
};

export default function AlimTalkTemplateManagement({
  view = "site",
  accountModeOverride,
}: AlimTalkTemplateManagementProps) {
  const { userInfo } = useAuthContext();
  const [account, setAccount] = useState<AlimTalkAccountResponse | null>(null);
  const [templates, setTemplates] = useState<AlimTalkTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailableMessage, setUnavailableMessage] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const loadGeneration = useRef(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AlimTalkTemplate | null>(null);
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [blueprints, setBlueprints] = useState<AlimTalkTemplateBlueprint[]>([]);
  const [blueprintsLoading, setBlueprintsLoading] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerCatalog, setProviderCatalog] = useState<AlimTalkProviderTemplateCatalogItem[]>([]);
  const [providerCatalogLoading, setProviderCatalogLoading] = useState(false);
  const [providerCatalogError, setProviderCatalogError] = useState("");
  const [selectedProviderCodes, setSelectedProviderCodes] = useState<string[]>([]);
  const [importingProviderTemplates, setImportingProviderTemplates] = useState(false);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setTemplates([]);
    setUnavailableMessage("");
    const [templatesResult, accountResult] = await Promise.allSettled([
      notificationCenterService.getAlimTalkTemplates({ page_size: 20, page, search: search || undefined, status: statusFilter || undefined }),
      accountModeOverride === undefined
        ? notificationCenterService.getAlimTalkAccount()
        : Promise.resolve(null),
    ]);

    if (generation !== loadGeneration.current) return;
    if (templatesResult.status === "fulfilled") {
      const data = templatesResult.value.data;
      setCount(Array.isArray(data) ? data.length : Number(data.count ?? data.results.length));
      setTemplates(asTemplateList(templatesResult.value.data));
    } else {
      if (templatesResult.reason?.response?.status === 404 && page > 1) {
        setPage(page - 1);
        return;
      }
      setTemplates([]);
      setUnavailableMessage(getErrorMessage(
        templatesResult.reason,
        "알림톡 템플릿 API를 사용할 수 없습니다.",
      ));
    }
    if (accountModeOverride !== undefined) {
      setAccount(null);
    } else if (accountResult.status === "fulfilled" && accountResult.value) {
      setAccount(accountResult.value.data);
    } else {
      setAccount(null);
    }
    setLoading(false);
  }, [accountModeOverride, page, search, statusFilter]);

  useEffect(() => {
    void load();
    return () => { loadGeneration.current += 1; };
  }, [load]);

  const mode = accountModeOverride ?? (account
    ? normalizeAlimTalkAccountMode(account.account_mode, account.using_default)
    : null);
  const isHeadquartersView = view === "headquarters";
  const canManage = isHeadquartersView
    ? userInfo?.is_hq_admin === true && mode === "HEADQUARTERS"
    : mode === "DIRECT" && hasCapability(userInfo, "site_owner");
  const modeLabel = mode === "HEADQUARTERS"
    ? "본사 공용 계정"
    : mode === "DIRECT"
      ? "지점 직접 계정"
      : "현재 알림톡 계정";

  const loadBlueprints = useCallback(async () => {
    if (!canManage || blueprints.length || blueprintsLoading) return;
    setBlueprintsLoading(true);
    try {
      const response = await notificationCenterService.getAlimTalkTemplateBlueprints();
      setBlueprints(response.data.results || []);
    } catch (error: any) {
      toast.error(getErrorMessage(error, "표준 템플릿 초안을 불러오지 못했습니다."));
    } finally {
      setBlueprintsLoading(false);
    }
  }, [blueprints.length, blueprintsLoading, canManage]);

  const openCreate = () => {
    if (!canManage) return;
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
    void loadBlueprints();
  };

  const applyBlueprint = (eventType: string) => {
    const blueprint = [...BENEFIT_TEMPLATE_BLUEPRINTS, ...blueprints].find((item) => item.event_type === eventType);
    if (!blueprint) return;
    setForm({
      template_code: blueprint.template_code,
      template_name: blueprint.template_name,
      template_content: blueprint.template_content,
      template_type: blueprint.template_type || "BA",
      category_code: blueprint.category_code || "008001",
      is_secure_template: Boolean(blueprint.is_secure_template),
      buttons: blueprint.buttons || [],
    });
  };

  const loadProviderCatalog = useCallback(async () => {
    setProviderCatalogLoading(true);
    setProviderCatalogError("");
    try {
      const response = await notificationCenterService.getAlimTalkProviderTemplateCatalog();
      setProviderCatalog(response.data.results || []);
      setSelectedProviderCodes([]);
    } catch (error: any) {
      setProviderCatalog([]);
      setProviderCatalogError(getErrorMessage(
        error,
        "DK 템플릿 목록을 불러오지 못했습니다.",
      ));
    } finally {
      setProviderCatalogLoading(false);
    }
  }, []);

  const openProviderCatalog = () => {
    if (!canManage) return;
    setProviderModalOpen(true);
    void loadProviderCatalog();
  };

  const toggleProviderCode = (templateCode: string) => {
    setSelectedProviderCodes((current) => (
      current.includes(templateCode)
        ? current.filter((code) => code !== templateCode)
        : [...current, templateCode]
    ));
  };

  const importProviderTemplates = async () => {
    if (!canManage) return;
    if (!selectedProviderCodes.length) {
      toast.error("가져올 DK 템플릿을 선택해 주세요.");
      return;
    }
    setImportingProviderTemplates(true);
    try {
      const response = await notificationCenterService.importAlimTalkProviderTemplates(
        selectedProviderCodes,
      );
      toast.success(`${response.data.imported_count}개 템플릿을 가져왔습니다.`);
      setProviderModalOpen(false);
      await load();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "DK 템플릿을 가져오지 못했습니다."));
    } finally {
      setImportingProviderTemplates(false);
    }
  };

  const openEdit = (template: AlimTalkTemplate) => {
    if (!canManage) return;
    setEditing(template);
    setForm({
      template_code: template.template_code || "",
      template_name: getAlimTalkTemplateName(template),
      template_content: getAlimTalkTemplateContent(template),
      template_type: template.template_type || "BA",
      category_code: template.category_code || "008001",
      is_secure_template: Boolean(template.is_secure_template),
      buttons: template.buttons || [],
    });
    setModalOpen(true);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManage) return;
    if (form.template_code.trim() && !/^[a-zA-Z0-9가-힣_()\-]{1,30}$/.test(form.template_code.trim())) {
      toast.error("템플릿 코드는 30자 이내의 영문, 숫자, 한글, 밑줄, 하이픈, 괄호만 사용할 수 있습니다.");
      return;
    }
    if (!form.template_name.trim() || !form.template_content.trim()) {
      toast.error("템플릿명과 내용을 입력해 주세요.");
      return;
    }
    if (form.template_content.length > 1000) {
      toast.error("템플릿 내용은 1,000자 이내로 입력해 주세요.");
      return;
    }
    const payload: AlimTalkTemplateUpsertPayload = {
      template_code: form.template_code.trim() || undefined,
      template_name: form.template_name.trim(),
      template_content: form.template_content,
      template_type: form.template_type,
      category_code: form.category_code,
      is_secure_template: form.is_secure_template,
      buttons: form.buttons,
    };
    setSaving(true);
    try {
      if (editing) {
        await notificationCenterService.updateAlimTalkTemplate(editing.id, payload);
        toast.success("알림톡 템플릿을 수정했습니다.");
      } else {
        await notificationCenterService.createAlimTalkTemplate(payload);
        toast.success("알림톡 템플릿을 등록했습니다.");
      }
      setModalOpen(false);
      await load();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "알림톡 템플릿을 저장하지 못했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const syncTemplate = async (template: AlimTalkTemplate) => {
    if (!canManage) return;
    setSyncingId(template.id);
    try {
      await notificationCenterService.syncAlimTalkTemplate(template.id);
      toast.success("카카오 검수 상태를 동기화했습니다.");
      await load();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "템플릿 상태를 동기화하지 못했습니다."));
    } finally {
      setSyncingId(null);
    }
  };

  const archiveTemplate = async (template: AlimTalkTemplate) => {
    if (!canManage) return;
    const confirmed = await dialogService.confirm({
      title: "템플릿을 보관하시겠습니까?",
      description: "신규 발송과 시스템 메시지 연결에서는 더 이상 선택할 수 없지만 기존 발송 이력은 유지됩니다.",
      confirmLabel: "보관",
      cancelLabel: "취소",
      tone: "destructive",
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await notificationCenterService.archiveAlimTalkTemplate(template.id);
      toast.success("알림톡 템플릿을 보관했습니다.");
      await load();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "템플릿을 보관하지 못했습니다."));
    }
  };

  const accessibleTemplates = useMemo(() => (
    canManage
      ? templates
      : templates.filter((template) => getAlimTalkTemplateStatus(template) === "APR")
  ), [canManage, templates]);
  const summary = useMemo(() => ({
    total: accessibleTemplates.length,
    approved: accessibleTemplates.filter((template) => getAlimTalkTemplateStatus(template) === "APR").length,
  }), [accessibleTemplates]);
  const visibleTemplates = accessibleTemplates;

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h5 className="mb-1">{isHeadquartersView ? "본사 알림톡 템플릿" : "알림톡 템플릿"}</h5>
          <p className="text-muted mb-0">
            {modeLabel}에서 사용할 템플릿과 카카오 검수 상태를 관리합니다.
          </p>
        </div>
        {canManage ? (
          <div className="d-flex flex-wrap gap-2">
            <Button color="soft-secondary" onClick={openProviderCatalog}>DK 템플릿 가져오기</Button>
            <Button color="primary" onClick={openCreate}>템플릿 등록</Button>
          </div>
        ) : null}
      </div>

      {mode === "HEADQUARTERS" && !canManage ? (
        <Alert color="info">본사 공용 템플릿은 HQ 관리자 페이지에서만 등록·수정할 수 있습니다.</Alert>
      ) : null}
      {isHeadquartersView && mode === "DIRECT" ? (
        <Alert color="info">현재 지점은 직접 계정을 사용 중입니다. 본사 공용 계정으로 전환한 뒤 본사 템플릿을 관리할 수 있습니다.</Alert>
      ) : null}
      {!isHeadquartersView && !canManage && mode !== "HEADQUARTERS" ? (
        <Alert color="info">발송에 사용할 수 있는 승인 완료 템플릿을 조회합니다.</Alert>
      ) : null}
      {unavailableMessage ? (
        <Alert color="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>{unavailableMessage}</span>
          <Button color="outline-warning" size="sm" onClick={() => void load()}>다시 시도</Button>
        </Alert>
      ) : null}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Input
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          onKeyDown={(event) => { if (event.key === "Enter") void load(); }}
          placeholder="템플릿명 또는 코드 검색"
          style={{ maxWidth: 320 }}
        />
        <Input aria-label="템플릿 상태" type="select" value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }} style={{ maxWidth: 160 }}><option value="">전체 상태</option><option value="APR">승인</option><option value="REG">등록</option><option value="REQ">검수 중</option><option value="REJ">반려</option></Input>
        <Button color="soft-primary" onClick={() => void load()} disabled={loading}>조회</Button>
        <span className="text-muted small">조회 {count}개 · 이 페이지 승인 {summary.approved}개</span>
      </div>

      <div className="table-responsive border rounded">
        <Table className="align-middle mb-0">
          <thead>
            <tr><th>템플릿</th><th>범위</th><th>변수</th><th>상태</th><th>수정일</th><th className="text-end">작업</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-4"><Spinner size="sm" /></td></tr> : null}
            {!loading && visibleTemplates.length === 0 ? <tr><td colSpan={6} className="text-center py-4 text-muted">{search.trim() ? "검색 결과가 없습니다." : "등록된 템플릿이 없습니다."}</td></tr> : null}
            {visibleTemplates.map((template) => {
              const variables = getAlimTalkTemplateVariables(template);
              const editable = canManage && template.editable !== false;
              return (
                <tr key={template.id}>
                  <td>
                    <strong>{getAlimTalkTemplateName(template) || `템플릿 ${template.id}`}</strong>
                    <div className="text-muted small">{template.template_code || "코드 미확인"}</div>
                    <div className="text-muted small text-truncate" style={{ maxWidth: 360 }}>{getAlimTalkTemplateContent(template)}</div>
                  </td>
                  <td>{String(template.scope || (mode === "HEADQUARTERS" ? "HEADQUARTERS" : "SITE")).toUpperCase() === "HEADQUARTERS" ? "본사" : "지점"}</td>
                  <td>{variables.length ? variables.map((variable) => <Badge key={variable.key} color="light" className="me-1">{variable.key}</Badge>) : "-"}</td>
                  <td>{getStatusBadge(getAlimTalkTemplateStatus(template))}</td>
                  <td className="text-nowrap">{template.updated_at ? new Date(template.updated_at).toLocaleString("ko-KR") : "-"}</td>
                  <td>
                    <div className="d-flex justify-content-end gap-1">
                      {canManage ? <Button color="soft-secondary" size="sm" onClick={() => void syncTemplate(template)} disabled={syncingId === template.id}>
                        {syncingId === template.id ? <Spinner size="sm" /> : "상태 동기화"}
                      </Button> : null}
                      {editable ? <Button color="soft-primary" size="sm" onClick={() => openEdit(template)}>수정</Button> : null}
                      {editable ? <Button color="outline-danger" size="sm" onClick={() => void archiveTemplate(template)}>보관</Button> : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <ListPagination label="템플릿" page={page} count={count} loading={loading} onChange={setPage} />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} variant="dialog" size="3xl" scrollable>
        <ModalHeader toggle={() => setModalOpen(false)}>{editing ? "알림톡 템플릿 수정" : "알림톡 템플릿 등록"}</ModalHeader>
        <form onSubmit={submit}>
          <ModalBody>
            <Row className="g-3">
              {!editing ? (
                <Col xs={12}>
                  <Label htmlFor="alimtalk-template-blueprint">템플릿 초안</Label>
                  <Input
                    id="alimtalk-template-blueprint"
                    type="select"
                    defaultValue=""
                    disabled={blueprintsLoading}
                    onChange={(event) => applyBlueprint(event.target.value)}
                  >
                    <option value="">{blueprintsLoading ? "초안 불러오는 중" : "직접 작성"}</option>
                    {[...BENEFIT_TEMPLATE_BLUEPRINTS, ...blueprints].map((blueprint) => (
                      <option key={blueprint.event_type} value={blueprint.event_type}>
                        {blueprint.event_label} ({blueprint.template_code})
                      </option>
                    ))}
                  </Input>
                  <div className="form-text">문구와 변수를 확인한 뒤 저장하세요. 혜택 수령 안내는 이벤트 참여 완료 고객에게만 발송할 수 있습니다.</div>
                </Col>
              ) : null}
              <Col md={7}>
                <Label htmlFor="alimtalk-template-name">템플릿명</Label>
                <Input id="alimtalk-template-name" value={form.template_name} onChange={(event) => setForm((current) => ({ ...current, template_name: event.target.value }))} required />
              </Col>
              <Col md={5}>
                <Label htmlFor="alimtalk-template-code">템플릿 코드</Label>
                <Input maxLength={30} id="alimtalk-template-code" value={form.template_code} onChange={(event) => setForm((current) => ({ ...current, template_code: event.target.value }))} placeholder="미입력 시 서버 생성" />
              </Col>
              <Col md={6}>
                <Label htmlFor="alimtalk-template-type">유형</Label>
                <Input id="alimtalk-template-type" type="select" value={form.template_type} onChange={(event) => setForm((current) => ({ ...current, template_type: event.target.value }))}>
                  <option value="BA">기본형</option><option value="EX">부가정보형</option><option value="AD">광고추가형</option><option value="MI">복합형</option>
                </Input>
              </Col>
              <Col md={6}>
                <Label htmlFor="alimtalk-template-category">카테고리 코드</Label>
                <Input id="alimtalk-template-category" value={form.category_code} onChange={(event) => setForm((current) => ({ ...current, category_code: event.target.value }))} placeholder="예: 008001" />
              </Col>
              <Col xs={12}>
                <Label htmlFor="alimtalk-template-content">템플릿 내용</Label>
                <Input id="alimtalk-template-content" type="textarea" rows={9} maxLength={1000} value={form.template_content} onChange={(event) => setForm((current) => ({ ...current, template_content: event.target.value }))} placeholder="#{CUSTOMER_NAME}님, 예약이 완료되었습니다." required />
                <div className="d-flex flex-wrap justify-content-between gap-2 form-text">
                  <span>변수는 {"#{event_name}"} 형식으로 입력해 주세요.</span>
                  <span>{form.template_content.length}/1,000자</span>
                </div>
              </Col>
              <Col xs={12}>
                <div className="form-check">
                  <Input id="alimtalk-template-secure" className="form-check-input" type="checkbox" checked={form.is_secure_template} onChange={(event) => setForm((current) => ({ ...current, is_secure_template: event.target.checked }))} />
                  <Label htmlFor="alimtalk-template-secure" className="form-check-label">보안 템플릿</Label>
                </div>
              </Col>
              <Col xs={12}>
                <ButtonsEditor key={editing?.id || "new"} channel="AT" value={form.buttons} onChange={(buttons) => setForm((current) => ({ ...current, buttons }))} />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={() => setModalOpen(false)} disabled={saving}>취소</Button>
            <Button type="submit" color="primary" disabled={saving}>{saving ? <><Spinner size="sm" className="me-2" />저장 중</> : "저장"}</Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={providerModalOpen} onClose={() => setProviderModalOpen(false)} variant="dialog" size="3xl" scrollable>
        <ModalHeader toggle={() => setProviderModalOpen(false)}>DK 템플릿 가져오기</ModalHeader>
        <ModalBody>
          <Alert color="info">
            DK에 이미 존재하는 템플릿만 현재 계정 범위로 가져옵니다. DK 템플릿을 새로 만들거나 수정하지 않습니다.
          </Alert>
          {providerCatalogError ? (
            <Alert color="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span>{providerCatalogError}</span>
              <Button color="outline-warning" size="sm" onClick={() => void loadProviderCatalog()}>다시 시도</Button>
            </Alert>
          ) : null}
          <div className="table-responsive border rounded">
            <Table className="align-middle mb-0">
              <thead>
                <tr><th style={{ width: 48 }}>선택</th><th>DK 템플릿</th><th>변수</th><th>DK 상태</th><th>로컬 상태</th></tr>
              </thead>
              <tbody>
                {providerCatalogLoading ? <tr><td colSpan={5} className="text-center py-4"><Spinner size="sm" /></td></tr> : null}
                {!providerCatalogLoading && !providerCatalogError && providerCatalog.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">DK에 조회된 템플릿이 없습니다.</td></tr>
                ) : null}
                {providerCatalog.map((template) => (
                  <tr key={template.template_code}>
                    <td>
                      <Input
                        className="form-check-input m-0"
                        type="checkbox"
                        aria-label={`${template.template_name} 가져오기`}
                        checked={selectedProviderCodes.includes(template.template_code)}
                        disabled={!template.importable}
                        onChange={() => toggleProviderCode(template.template_code)}
                      />
                    </td>
                    <td>
                      <strong>{template.template_name}</strong>
                      <div className="text-muted small">{template.template_code}</div>
                      <div className="text-muted small text-truncate" style={{ maxWidth: 360 }}>{template.template_content}</div>
                    </td>
                    <td>{template.ordered_variables.length ? template.ordered_variables.map((variable) => <Badge key={variable} color="light" className="me-1">{variable}</Badge>) : "-"}</td>
                    <td>{getStatusBadge(template.status)}</td>
                    <td>{template.importable ? "가져오기 필요" : `등록됨 · ${template.local_status || "상태 미확인"}`}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={() => setProviderModalOpen(false)} disabled={importingProviderTemplates}>취소</Button>
          <Button type="button" color="primary" onClick={() => void importProviderTemplates()} disabled={importingProviderTemplates || !selectedProviderCodes.length}>
            {importingProviderTemplates ? <><Spinner size="sm" className="me-2" />가져오는 중</> : `선택 ${selectedProviderCodes.length}개 가져오기`}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
